import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import WorkOrderDialog from "@/mechanic/components/WorkOrderDialog";
import { ArrowLeft, Plus, Sparkles, Car, Phone, Mail, Pencil, Trash2 } from "lucide-react";

const vBlank = {
  year: "", make: "", model: "", trim: "", color: "",
  plate: "", vin: "", mileage: "", engine: "", notes: "",
};

export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["mech-customer-full", customerId],
    queryFn: async () => (await api.get(`/customers/${customerId}/full`)).data,
    enabled: !!customerId,
  });

  const [vOpen, setVOpen] = useState(false);
  const [vEdit, setVEdit] = useState(null);
  const [vForm, setVForm] = useState(vBlank);
  const [vinBusy, setVinBusy] = useState(false);
  const [woOpen, setWoOpen] = useState(false);
  const [woPreset, setWoPreset] = useState(null);
  const [woEdit, setWoEdit] = useState(null);

  const openNewVehicle = () => { setVEdit(null); setVForm(vBlank); setVOpen(true); };
  const openEditVehicle = (v) => {
    setVEdit(v);
    setVForm({ ...vBlank, ...v, year: v.year || "", mileage: v.mileage || "" });
    setVOpen(true);
  };

  const decodeVin = async () => {
    const vin = (vForm.vin || "").trim();
    if (vin.length < 5) { toast.error("Enter a VIN"); return; }
    setVinBusy(true);
    try {
      const d = (await api.post("/vin/decode", { vin })).data;
      const eng = [d.engine ? `${d.engine}L` : "", d.cylinders ? `${d.cylinders}cyl` : "", d.fuel]
        .filter(Boolean).join(" · ");
      setVForm((f) => ({
        ...f,
        vin: d.vin || f.vin,
        year: d.year ? String(d.year) : f.year,
        make: d.make || f.make,
        model: d.model || f.model,
        trim: d.trim || f.trim,
        engine: eng || f.engine,
      }));
      toast.success(`VIN decoded: ${d.year || ""} ${d.make} ${d.model}${eng ? " · " + eng : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "VIN lookup failed"); }
    setVinBusy(false);
  };

  const saveVeh = useMutation({
    mutationFn: async () => {
      const c = data?.customer;
      const body = {
        ...vForm,
        customer_id: customerId,
        customer_name: c?.name || "",
        year: vForm.year ? Number(vForm.year) : null,
        mileage: vForm.mileage ? Number(vForm.mileage) : null,
      };
      return vEdit ? api.put(`/vehicles/${vEdit.id}`, body) : api.post("/vehicles", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-customer-full", customerId] });
      qc.invalidateQueries({ queryKey: ["mech-ext"] });
      toast.success("Saved");
      setVOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const delVeh = useMutation({
    mutationFn: (id) => api.delete(`/vehicles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mech-customer-full", customerId] });
      toast.success("Deleted");
    },
  });

  const openNewWO = (vehicle) => {
    setWoEdit(null);
    setWoPreset({ source: "external", ref: vehicle.id, label: `${vehicle.year || ""} ${vehicle.make} ${vehicle.model}`.trim() });
    setWoOpen(true);
  };
  const openEditWO = (wo) => {
    setWoEdit(wo);
    setWoPreset(null);
    setWoOpen(true);
  };

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Customer not found.</div>;

  const c = data.customer;
  const vehicles = data.vehicles || [];
  const wos = data.work_orders || [];
  const s = data.stats || {};

  return (
    <div className="p-8" data-testid="customer-detail-page">
      <Button variant="ghost" onClick={() => navigate("/mechanic/customers")} className="mb-4" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4 mr-1" />Back to customers
      </Button>

      {/* Header card */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl mb-2" data-testid="customer-name">{c.name}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
              {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div><div className="text-2xl font-bold" data-testid="stat-vehicles">{s.vehicle_count}</div><div className="text-xs text-muted-foreground uppercase">Vehicles</div></div>
            <div><div className="text-2xl font-bold" data-testid="stat-wos">{s.wo_count}</div><div className="text-xs text-muted-foreground uppercase">Work Orders</div></div>
            <div><div className="text-2xl font-bold font-mono-data text-emerald-400" data-testid="stat-billed">{money(s.total_billed)}</div><div className="text-xs text-muted-foreground uppercase">Billed</div></div>
          </div>
        </div>
      </Card>

      {/* Vehicles */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl">Vehicles</h2>
        <Button onClick={openNewVehicle} data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-1" />Add vehicle</Button>
      </div>
      {vehicles.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground mb-6" data-testid="no-vehicles">
          No vehicles yet. Add the customer&apos;s car — VIN decode fills the rest automatically.
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {vehicles.map((v) => (
            <Card key={v.id} className="p-4" data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="font-heading truncate">{v.year} {v.make} {v.model} {v.trim ? <span className="text-muted-foreground">· {v.trim}</span> : null}</div>
                </div>
                <div>
                  <Button size="sm" variant="ghost" onClick={() => openEditVehicle(v)} data-testid={`edit-vehicle-${v.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete this vehicle?`)) delVeh.mutate(v.id); }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground font-mono mb-3 space-y-0.5">
                <div>{v.plate || "—"}{v.color ? ` · ${v.color}` : ""}</div>
                {v.engine && <div>{v.engine}</div>}
                {v.vin && <div>VIN {v.vin}</div>}
                {v.mileage ? <div>{v.mileage.toLocaleString()} km</div> : null}
              </div>
              <Button size="sm" onClick={() => openNewWO(v)} className="w-full" data-testid={`new-wo-for-${v.id}`}>
                <Plus className="h-3.5 w-3.5 mr-1" />New Work Order
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Work orders */}
      <h2 className="font-heading text-xl mb-3">Work order history</h2>
      {wos.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">No work orders yet.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/30">
              <tr>
                <th className="text-left px-3 py-2 label-mono font-normal">Date</th>
                <th className="text-left px-3 py-2 label-mono font-normal">Vehicle</th>
                <th className="text-left px-3 py-2 label-mono font-normal">Complaint</th>
                <th className="text-left px-3 py-2 label-mono font-normal">Status</th>
                <th className="text-right px-3 py-2 label-mono font-normal">Total</th>
              </tr>
            </thead>
            <tbody>
              {wos.map((w) => (
                <tr key={w.id} className="border-t border-border hover:bg-secondary/20 cursor-pointer" onClick={() => openEditWO(w)} data-testid={`wo-row-${w.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{fmtDate(w.service_date || w.created_at)}</td>
                  <td className="px-3 py-2">{w.vehicle_label || "—"}</td>
                  <td className="px-3 py-2 truncate max-w-xs">{w.complaint || w.diagnosis || "—"}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px]">{w.status}</Badge></td>
                  <td className="px-3 py-2 text-right font-mono-data">{money(w.totals?.charge || w.totals?.total || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Vehicle dialog */}
      <Dialog open={vOpen} onOpenChange={setVOpen}>
        <DialogContent className="max-w-2xl" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{vEdit ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-6 space-y-1">
              <Label className="label-mono">VIN (auto-decodes Year / Make / Model / Trim / Engine)</Label>
              <div className="flex gap-2">
                <Input className="flex-1" value={vForm.vin} onChange={(e) => setVForm((f) => ({ ...f, vin: e.target.value.toUpperCase() }))} data-testid="vd-vin" />
                <Button type="button" size="sm" variant="secondary" onClick={decodeVin} disabled={vinBusy || !vForm.vin.trim()} data-testid="vd-decode">
                  <Sparkles className="h-3.5 w-3.5 mr-1" />{vinBusy ? "Decoding…" : "Decode"}
                </Button>
              </div>
            </div>
            <div className="col-span-1"><Label className="label-mono">Year</Label><Input value={vForm.year} onChange={(e) => setVForm((f) => ({ ...f, year: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="label-mono">Make</Label><Input value={vForm.make} onChange={(e) => setVForm((f) => ({ ...f, make: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="label-mono">Model</Label><Input value={vForm.model} onChange={(e) => setVForm((f) => ({ ...f, model: e.target.value }))} /></div>
            <div className="col-span-1"><Label className="label-mono">Trim</Label><Input value={vForm.trim} onChange={(e) => setVForm((f) => ({ ...f, trim: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="label-mono">Color</Label><Input value={vForm.color} onChange={(e) => setVForm((f) => ({ ...f, color: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="label-mono">Plate</Label><Input value={vForm.plate} onChange={(e) => setVForm((f) => ({ ...f, plate: e.target.value }))} /></div>
            <div className="col-span-2"><Label className="label-mono">Mileage</Label><Input type="number" value={vForm.mileage} onChange={(e) => setVForm((f) => ({ ...f, mileage: e.target.value }))} /></div>
            <div className="col-span-6"><Label className="label-mono">Engine</Label><Input value={vForm.engine} onChange={(e) => setVForm((f) => ({ ...f, engine: e.target.value }))} placeholder="e.g. 2.0L 4cyl Gasoline" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVOpen(false)}>Cancel</Button>
            <Button onClick={() => saveVeh.mutate()} disabled={saveVeh.isPending || !vForm.make || !vForm.model} data-testid="vd-save">
              {saveVeh.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WO dialog */}
      <WorkOrderDialog
        open={woOpen}
        onOpenChange={setWoOpen}
        editing={woEdit}
        presetVehicle={woPreset}
        onSaved={() => qc.invalidateQueries({ queryKey: ["mech-customer-full", customerId] })}
      />
    </div>
  );
}
